/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { type HttpResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { UtilService } from './util.service';
import { StorageEnum } from '../enums/storage.enum';
import { isPlatformBrowser } from '@angular/common';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

/**
 * Manages application data persistence across browser sessions and native mobile platforms.
 * Provides unified API for session storage, local storage, and in-memory HTTP caching.
 * Handles cross-platform storage via Capacitor Preferences on native platforms and Web Storage APIs on browser.
 */
@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private httpCache: Map<string, HttpResponse<any>>;
  private activeStore: any;
  private useLocalStorage = false;
  storageInitialized = false;
  isBrowser = false;
  hasDeepLinkRedirect = signal(false);

  /** In-memory cache for local storage to maintain sync API */
  private readonly localCache: Map<string, string> = new Map<string, string>();
  /** Whether we're running on a native mobile platform */
  private readonly isNativePlatform: boolean;
  /** Promise that resolves when local cache is initialized */
  // eslint-disable-next-line @typescript-eslint/prefer-readonly -- reassigned conditionally in constructor
  private cacheReady: Promise<void> = Promise.resolve();

  constructor(
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    private readonly utils: UtilService,
  ) {
    this.httpCache = new Map<string, HttpResponse<any>>();
    this.activeStore = {};
    this.isBrowser = isPlatformBrowser(platformId);
    this.isNativePlatform = Capacitor.isNativePlatform();
    this.storageInitialized = true;

    // Initialize cache from Capacitor Preferences on native platforms
    if (this.isBrowser && this.isNativePlatform) {
      this.cacheReady = this.initializeLocalCache();
    }
  }

  /**
   * Initialize in-memory cache from Capacitor Preferences (native platforms only).
   * This allows sync reads while using native storage.
   */
  private async initializeLocalCache(): Promise<void> {
    try {
      const { keys } = await Preferences.keys();
      for (const key of keys) {
        const { value } = await Preferences.get({ key });
        if (value !== null) {
          this.localCache.set(key, value);
        }
      }
      this.utils.consoleLog('Local storage cache initialized from Capacitor Preferences');
    } catch (e) {
      this.utils.consoleGroup('ERROR initializing local cache from Preferences', e);
    }
  }

  /**
   * Returns a promise that resolves when local storage cache is initialized.
   * On web platforms, resolves immediately. On native platforms, waits for
   * Capacitor Preferences to load into memory cache.
   */
  async whenReady(): Promise<void> {
    await this.cacheReady;
  }

  /**
   * Configures whether storage operations should also persist to local storage.
   * When enabled, set/remove operations update both active store and local storage.
   * @param useLocalStorage - Flag to enable local storage persistence (default: false)
   */
  preserveStorage(useLocalStorage = false): void {
    this.useLocalStorage = useLocalStorage;
  }

  /**
   * Converts data to an encrypted string representation for storage.
   * Currently uses JSON serialization (encryption layer removed for stability).
   * @param data - The raw data object to encrypt
   * @returns string - JSON-serialized encrypted data string
   */
  private encryptData(data: any): string {
    return JSON.stringify(data);
    // removing for now success data was causing issues with encryption
    // const stringifiedData = JSON.stringify(data);
    // return btoa(stringifiedData);
  }

  /**
   * Converts an encrypted data string back to its original form.
   * Currently uses JSON deserialization (encryption layer removed for stability).
   * @param encryptedData - A JSON-serialized encrypted data string
   * @returns any - The deserialized original data object
   */
  private decryptData(encryptedData: string): any {
    // removing for now success data was causing issues with encryption
    // const stringifiedData = atob(encryptedData);
    return JSON.parse(encryptedData);
  }

  /* region http cache */
  /**
   * Caches an HTTP response for a specific key.
   * @param key - The cache key identifier
   * @param value - The HTTP response to cache
   * @returns boolean - true if cached successfully, false if key or value is invalid
   */
  setCache(key: string, value: HttpResponse<any>): boolean {
    if (!this.utils.isNullOrWhitespace(key) && !this.utils.isNullOrUndefined(value)) {
      this.httpCache.set(key, value);
      return true;
    }
    return false;
  }

  /**
   * Retrieves a cached HTTP response by key.
   * @param key - The cache key identifier
   * @returns HttpResponse<any> | null - A deep copy of the cached response, or null if not found
   */
  getCache(key: string): HttpResponse<any> | null {
    if (!this.utils.isNullOrWhitespace(key) && this.httpCache.has(key)) {
      return JSON.parse(JSON.stringify(this.httpCache.get(key)));
    }
    return null;
  }

  /**
   * Clears all cached HTTP responses.
   * @returns boolean - true when cache is cleared
   */
  clearCache(): boolean {
    this.httpCache = new Map<string, HttpResponse<any>>();
    return true;
  }
  /* endregion http cache */

  /* region session */
  /**
   * Stores data in browser session storage (cleared when session ends).
   * Data is encrypted before storage. Only available in browser environments.
   * @param key - StorageEnum key identifier for the data
   * @param value - The data object to store
   * @returns boolean - true if stored successfully, false if in non-browser environment or error occurs
   */
  setSession(key: StorageEnum, value: any): boolean {
    try {
      if (this.isBrowser) {
        const encryptedData = this.encryptData(value);
        sessionStorage.setItem(key.toString(), encryptedData);
        return true;
      }
      return false;
    } catch (e) {
      this.utils.consoleGroup('ERROR in StorageService.setSession', e);
      return false;
    }
  }

  /**
   * Retrieves data from browser session storage.
   * Returns a deep copy of the stored object; modify the copy and call setSession() to update.
   * @param key - StorageEnum key identifier for the data
   * @returns any - The decrypted data object, or null if not found or in non-browser environment
   */
  getSession(key: StorageEnum): any {
    try {
      if (this.isBrowser) {
        const encryptedData = sessionStorage.getItem(key.toString());
        if (encryptedData !== null) {
          return this.decryptData(encryptedData);
        } else {
          return null;
        }
      }
      return null;
    } catch (e) {
      this.utils.consoleGroup('ERROR in StorageService.getSession', e);
      return null;
    }
  }

  /**
   * Removes a specific key from browser session storage.
   * @param key - StorageEnum key identifier to remove
   */
  removeSession(key: StorageEnum) {
    if (this.isBrowser) {
      sessionStorage.removeItem(key.toString());
      this.utils.consoleLog(`${key} removed from session storage!`);
    }
  }

  /**
   * Clears all session storage except GlobalVars and DeepLink (which are preserved and restored).
   * Resets the hasDeepLinkRedirect signal when applicable.
   */
  clearAllSession() {
    if (this.isBrowser) {
      const globalVars = this.getSession(StorageEnum.GlobalVars);
      const deepLinkRedirect = this.getSession(StorageEnum.DeepLink);
      sessionStorage.clear();
      if (globalVars) {
        this.setSession(StorageEnum.GlobalVars, globalVars);
      }
      if (deepLinkRedirect && this.hasDeepLinkRedirect()) {
        this.setSession(StorageEnum.DeepLink, deepLinkRedirect);
        this.hasDeepLinkRedirect.set(false);
      }
      this.utils.consoleLog('All stored session data has be cleared!');
    }
  }
  /* endregion session */

  /* region local */
  /**
   * Stores data in persistent local storage (survives browser/app restarts).
   * Uses Capacitor Preferences on native platforms, browser localStorage on web.
   * Maintains synchronous API by updating in-memory cache immediately with async writes.
   * @param key - StorageEnum key identifier for the data
   * @param value - The data object to store
   * @returns boolean - true if stored successfully, false if in non-browser environment or error occurs
   */
  setLocal(key: StorageEnum, value: any): boolean {
    try {
      if (this.isBrowser) {
        const encryptedData = this.encryptData(value);

        if (this.isNativePlatform) {
          // Update cache immediately for sync reads
          this.localCache.set(key.toString(), encryptedData);
          // Fire-and-forget write to Capacitor Preferences
          Preferences.set({ key: key.toString(), value: encryptedData }).catch((e) => {
            this.utils.consoleGroup('ERROR in StorageService.setLocal (Preferences)', e);
          });
        } else {
          // Web: use localStorage directly
          localStorage.setItem(key.toString(), encryptedData);
        }
        return true;
      }
      return false;
    } catch (e) {
      this.utils.consoleGroup('ERROR in StorageService.setLocal', e);
      return false;
    }
  }

  /**
   * Retrieves data from persistent local storage.
   * Uses Capacitor Preferences on native platforms, browser localStorage on web.
   * Returns a deep copy of the stored object; modify the copy and call setLocal() to update.
   * @param key - StorageEnum key identifier for the data
   * @returns any - The decrypted data object, or null if not found or in non-browser environment
   */
  getLocal(key: StorageEnum): any {
    try {
      if (this.isBrowser) {
        let encryptedData: string | null = null;

        if (this.isNativePlatform) {
          // Read from in-memory cache (populated from Preferences on init)
          encryptedData = this.localCache.get(key.toString()) ?? null;
        } else {
          // Web: use localStorage directly
          encryptedData = localStorage.getItem(key.toString());
        }

        if (encryptedData !== null) {
          return this.decryptData(encryptedData);
        }
        return null;
      }
      return null;
    } catch (e) {
      this.utils.consoleGroup('ERROR in StorageService.getLocal', e);
      return null;
    }
  }

  /**
   * Removes a specific key from persistent local storage.
   * Uses Capacitor Preferences on native platforms, browser localStorage on web.
   * @param key - StorageEnum key identifier to remove
   */
  removeLocal(key: StorageEnum) {
    if (this.isBrowser) {
      if (this.isNativePlatform) {
        // Update cache immediately
        this.localCache.delete(key.toString());
        // Fire-and-forget remove from Capacitor Preferences
        Preferences.remove({ key: key.toString() }).catch((e) => {
          this.utils.consoleGroup('ERROR in StorageService.removeLocal (Preferences)', e);
        });
      } else {
        // Web: use localStorage directly
        localStorage.removeItem(key.toString());
      }
      this.utils.consoleLog(`${key} removed from local storage!`);
    }
  }

  /**
   * Clears all persistent local storage.
   * Uses Capacitor Preferences on native platforms, browser localStorage on web.
   */
  clearAllLocal() {
    if (this.isBrowser) {
      if (this.isNativePlatform) {
        // Clear cache immediately
        this.localCache.clear();
        // Fire-and-forget clear Capacitor Preferences
        Preferences.clear().catch((e) => {
          this.utils.consoleGroup('ERROR in StorageService.clearAllLocal (Preferences)', e);
        });
      } else {
        // Web: use localStorage directly
        localStorage.clear();
      }
      this.utils.consoleLog('All stored local data has be cleared!');
    }
  }

  /**
   * Logs all keys currently stored in local storage to the console.
   * Reads from in-memory cache on native, localStorage on web.
   */
  printKeysLocal() {
    if (this.isBrowser) {
      if (this.isNativePlatform) {
        const keys = Array.from(this.localCache.keys());
        this.utils.consoleLog(`Keys in Storage: ${keys.toString()}`);
      } else {
        const keys = Object.keys(localStorage);
        this.utils.consoleLog(`Keys in Storage: ${keys.toString()}`);
      }
    }
  }

  /* endregion local */

  /**
   * Stores data in the active in-memory store.
   * Optionally persists to local storage if enabled via preserveStorage().
   * @param key - StorageEnum key identifier for the data
   * @param value - The data object to store
   */
  set(key: StorageEnum, value: any) {
    this.activeStore[key] = value;
    if (this.useLocalStorage) {
      this.setLocal(key, value);
    }
  }

  /**
   * Retrieves data from the active in-memory store.
   * Falls back to local storage if key not found and local storage is enabled.
   * Returns a deep copy of the stored object; modify the copy and call set() to update.
   * @param key - StorageEnum key identifier for the data
   * @returns any - The data object, or null if not found
   */
  get(key: StorageEnum) {
    if (Object.prototype.hasOwnProperty.call(this.activeStore, key)) {
      return JSON.parse(JSON.stringify(this.activeStore[key]));
    }
    this.utils.consoleLog(`Key '${key}' not found in active storage!`);
    if (this.useLocalStorage) {
      return this.getLocal(key);
    }
    return null;
  }

  /**
   * Removes a specific key from the active in-memory store.
   * Optionally removes from local storage if enabled via preserveStorage().
   * @param key - StorageEnum key identifier to remove
   */
  remove(key: StorageEnum) {
    if (Object.prototype.hasOwnProperty.call(this.activeStore, key)) {
      Reflect.deleteProperty(this.activeStore, key);
      this.utils.consoleLog(`Key '${key}' deleted from active storage!`);
    } else {
      this.utils.consoleLog(`Key '${key}' not found in active storage!`);
    }
    if (this.useLocalStorage) {
      this.removeLocal(key);
    }
  }

  /**
   * Clears all data from the active in-memory store.
   * Optionally clears local storage if enabled via preserveStorage().
   */
  clear() {
    this.activeStore = {};
    this.utils.consoleLog('Active storage has been cleared!');
    if (this.useLocalStorage) {
      this.clearAllLocal();
    }
  }

  /**
   * Logs the active in-memory store and local storage keys to the console.
   * Useful for debugging storage state.
   */
  printStore() {
    this.utils.consoleGroup('Active Storage', this.activeStore);
    if (this.useLocalStorage) {
      this.printKeysLocal();
    }
  }
}
